/** The `tenant · … · ID n` identity row every detail page renders under its
 *  title, with the operator and the id as value-action triggers.
 *
 *      <IdentitySubtitle
 *        tenant={tour.tenant}
 *        tenantFallback="Unassigned"
 *        grid={{ href: toursHref([], tour.tenant?.id), label: 'Tours of this operator' }}
 *        id={tour.id}
 *      />
 *
 *  `grid` names the list this page belongs to, so the operator part offers
 *  "open operator" and "<records> of this operator"; `extra` slots further
 *  parts between the tenant and the id (a category, a code, …). */
import type { ReactNode } from 'react';

import { act, DetailSubtitle, ValueActions, type ValueAction } from './ValueActions';

export interface OperatorRef {
  id: number;
  name?: string | null;
  code?: string | null;
}

export interface GridLink {
  href: string;
  label: string;
}

/** Open the operator, list this page's records for it, copy its code. */
export function operatorActions(tenant: OperatorRef, grid?: GridLink | null): ValueAction[] {
  const out: ValueAction[] = [act.open(`/tour-operators/${tenant.id}`, 'Open operator')];
  if (grid) out.push(act.filter(grid.href, grid.label));
  if (tenant.code) out.push(act.copy(tenant.code, 'Copy operator code'));
  return out;
}

export function IdentitySubtitle({
  tenant,
  tenantFallback = 'Unassigned',
  grid,
  id,
  idLabel = 'Copy id',
  extra = [],
}: {
  tenant: OperatorRef | null | undefined;
  /** Shown (plain) when there is no operator: "Unassigned", "System", … */
  tenantFallback?: string;
  grid?: GridLink | null;
  id: number | string;
  idLabel?: string;
  extra?: ReactNode[];
}) {
  const tenantPart = tenant ? (
    <ValueActions actions={operatorActions(tenant, grid)}>{tenant.name ?? tenant.code ?? `#${tenant.id}`}</ValueActions>
  ) : (
    tenantFallback
  );
  const idPart = <ValueActions actions={[act.copy(String(id), idLabel)]}>ID {id}</ValueActions>;
  return <DetailSubtitle parts={[tenantPart, ...extra, idPart]} />;
}
